import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { FileCheck } from "lucide-react";

export default function DealerCertificateTab({ dealerId }) {
  const [certs, setCerts] = useState(null);

  useEffect(() => {
    api.get("/admin/certificates", { params: { dealer_id: dealerId } })
      .then((r) => setCerts(r.data))
      .catch(() => setCerts([]));
  }, [dealerId]);

  if (certs === null) return null;
  const current = certs.find((c) => c.status === "APPROVED" && !c.replaced_by_certificate_id);
  const pending = certs.find((c) => c.status === "PENDING_REVIEW");

  return (
    <div className="max-w-xl rounded-lg border border-border bg-white p-6" data-testid="dealer-certificate-tab">
      <div className="flex items-center gap-3">
        <FileCheck className="h-5 w-5 text-terracotta" />
        <h3 className="text-sm font-semibold uppercase tracking-wider text-warmgrey">Resale certificate</h3>
      </div>
      {certs.length === 0 ? (
        <p className="mt-4 text-sm text-warmgrey" data-testid="dealer-certificate-none">
          No certificate uploaded yet — purchases will include applicable sales tax until a resale exemption is approved.
        </p>
      ) : (
        <div className="mt-4 space-y-3 text-sm" data-testid="dealer-certificate-latest">
          {current && (
            <div className="rounded-md border border-border bg-sand p-3" data-testid="dealer-certificate-current">
              <p className="text-charcoal">
                <span className="text-warmgrey">Current:</span>{" "}
                <span className="font-medium" data-testid="dealer-certificate-status">
                  {current.status}
                </span>
                {current.expiration_date && (
                  <span className="num"> — expires {current.expiration_date}
                    {current.days_remaining != null && ` (${current.days_remaining}d)`}
                  </span>
                )}
              </p>
              {current.certificate_number && (
                <p className="mt-1 text-xs text-warmgrey num" data-testid="dealer-certificate-number">
                  Certificate #{current.certificate_number}{current.state && ` · ${current.state}`}
                </p>
              )}
              {current.days_remaining != null && current.days_remaining <= 30 && (
                <p className="mt-1 text-xs text-status-reserved" data-testid="dealer-certificate-renewal-due">
                  Renewal due — {current.days_remaining > 0 ? `${current.days_remaining} days remaining` : "expired"}
                </p>
              )}
            </div>
          )}
          {pending && (
            <p className="text-warmgrey" data-testid="dealer-certificate-pending-replacement">
              {pending.replaces_certificate_id ? "Replacement" : "Certificate"} pending review:{" "}
              <span className="num">{pending.original_filename}</span>
            </p>
          )}
          <ul className="space-y-1.5 border-t border-border pt-3" data-testid="dealer-certificate-history">
            {certs.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-2 text-xs text-warmgrey">
                <span className="num">{new Date(c.created_at).toLocaleDateString()}</span>
                <span className={`badge-status ${c.status === "APPROVED" ? "badge-available" : "badge-neutral"}`}>{c.status}</span>
                {c.state && <span className="badge-status badge-neutral">{c.state}</span>}
                <span className="num">{c.original_filename}</span>
                {c.certificate_number && <span className="num">#{c.certificate_number}</span>}
                {c.expiration_date && <span className="num">exp {c.expiration_date}</span>}
                {c.replaced_by_certificate_id && <span>→ replaced</span>}
              </li>
            ))}
          </ul>
          <Link to="/admin/certificates" className="inline-block pt-1 text-sm font-medium text-terracotta underline" data-testid="dealer-certificate-queue-link">
            Open certificates queue
          </Link>
        </div>
      )}
    </div>
  );
}
